import { useLatest } from '@qfa/react-components';
import booleanIntersects from '@turf/boolean-intersects';
import { Geometry } from '@turf/helpers';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getHtmlDataUrl, isInCacheOrWorking } from '../BaseMap/utils';
import {
  KineticGeoJsonData,
  KineticMarkerData,
  MapMarkerLayer,
  MapMarkers,
} from './MapMarkerContext';

type Data = KineticMarkerData | KineticGeoJsonData;

// todo extract function
export const useMapMarkerContext = (selectedLayers: string[]) => {
  /** This is a master mapping of all marker data we have, organized by layerId */
  const [mapMarkers, setMapMarkers] = useState<MapMarkers>({});
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const addToCache = useCallback(async (html: string) => {
    // if the icon is not in the cache, add it.
    // trigger an update after complete.
    if (!isInCacheOrWorking(html)) {
      await getHtmlDataUrl(html);
      setLastUpdate(new Date());
    }
  }, []);

  const selectedLayersRef = useLatest(selectedLayers);

  const prevSelectedLayers = useRef<string[]>([]);

  /**
   * key: layerId
   * value: No. of times the status layer has been selected (used as version for layer data)
   */
  const layerSelectedCountMap = useRef<Record<string, number>>({});

  useEffect(() => {
    selectedLayers.forEach((selectedLayer) => {
      if (!prevSelectedLayers?.current?.includes(selectedLayer)) {
        layerSelectedCountMap.current[selectedLayer] =
          (layerSelectedCountMap.current[selectedLayer] ?? 0) + 1;
      }
    });
    prevSelectedLayers.current = selectedLayers;
    setLastUpdate(new Date());
  }, [selectedLayers]);

  /** trim down the data we pass to the marker layer, only show data for layers that are enabled */
  const renderedMarkerData = useMemo(
    () => {
      return Object.keys(mapMarkers)
        .filter((layer) => selectedLayers.includes(layer))
        .reduce((acc, layer) => {
          const layerData = mapMarkers[layer].data;
          layerData.forEach((v) => {
            acc.set(v.id, v);
          });
          return acc;
        }, new Map<string, Data>());
    },
    // only recalculate when lastUpdate changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastUpdate]
  );

  const addLayer = useCallback(
    (layerId: string, layer: MapMarkerLayer) => {
      // add all icons to cache.
      const version = layerSelectedCountMap.current[layerId];
      layer.data.forEach((value: KineticMarkerData) => {
        addToCache(value.iconHtml);
      });
      setMapMarkers((prev) => {
        const newMarkers = {
          ...prev,
          [layerId]: layer,
        };

        // Check if the layer already exists in previous markers
        if (prev[layerId]) {
          /**
           * Merge the data from the existing layer and the new layer
           * i) If the existing data is from the previous time when the layer was enabled (here referred as previous version), then we use `queryData` as it is latest than the `existingData`
           * ii) If the existing data is from the subscription updates received before we received the query response (due to response latency), then we will use the `existingData` as it latest than the `queryData`.
           */
          const mergedData = new Map<string, Data>(prev[layerId].data);
          layer.data.forEach((queryData: KineticMarkerData, id: string) => {
            // Only add query data to the merged data if it doesn't already exist or hasn't been modified by subscription
            const existingData = mergedData.get(id);
            const hasExistingData = !!existingData;
            const updatedBySubscription = existingData?.version && existingData?.version >= version; //This means that we received a subscription data before we got response for the query
            if (!hasExistingData || !updatedBySubscription) {
              mergedData.set(id, queryData);
            }
          });

          newMarkers[layerId] = {
            ...layer,
            data: mergedData,
          };
        }
        const newData = new Map<string, Data>();
        Object.keys(newMarkers)
          .filter((layer) => selectedLayersRef.current.includes(layer))
          .forEach((layer) =>
            newMarkers[layer].data.forEach((value: KineticMarkerData, id: string) => {
              newData.set(id, value);
            })
          );
        return {
          ...prev,
          [layerId]: layer,
        };
      });
      setLastUpdate(new Date());
    },
    [addToCache, selectedLayersRef]
  );

  /**
   * Update the data for a layer.
   */
  const updateData = useCallback(
    (layerId: string, updatedData: Map<string, KineticMarkerData>) => {
      const version = layerSelectedCountMap.current[layerId];
      const mapMarkerData = mapMarkers[layerId]?.data;

      if (!mapMarkerData) {
        updatedData.forEach((value) => {
          value.version = version;
          addToCache(value.iconHtml);
        });

        setMapMarkers((previousMarkers) => {
          let updatedMapMarkers = { ...previousMarkers };
          updatedMapMarkers[layerId] = {
            data: updatedData,
          };
          return updatedMapMarkers;
        });
        setLastUpdate(new Date());
        return;
      }
      if (!selectedLayersRef.current.includes(layerId)) {
        return;
      }
      console.log({ layerId, updatedData });

      setMapMarkers((previousMarkers) => {
        let updatedMapMarkers = { ...previousMarkers };
        const newMarkerData = new Map<string, any>(updatedMapMarkers[layerId]?.data ?? []);
        updatedData.forEach((value: KineticMarkerData, key) => {
          // make sure the marker is in cache
          addToCache(value.iconHtml);
          const prev = newMarkerData.get(key);
          // combine old and new state.
          if (isEqual(prev, value)) {
            return;
          }
          value.version = version;
          const newValue = { ...prev, ...value, version };
          newMarkerData.set(key, newValue);
          if (!updatedMapMarkers[layerId]?.data) {
            updatedMapMarkers[layerId] = {
              data: newMarkerData,
            };
            return;
          }
          updatedMapMarkers[layerId].data = newMarkerData;
        });
        return updatedMapMarkers;
      });

      setLastUpdate(new Date());
    },
    [addToCache, selectedLayersRef, mapMarkers]
  );

  const removeLayer = useCallback((layerId: string) => {
    setMapMarkers((prev) => {
      const mapMarker = { ...prev };
      if (!mapMarker[layerId]) {
        return mapMarker;
      }
      delete mapMarker[layerId];
      return mapMarker;
    });
    setLastUpdate(new Date());
  }, []);

  const intersectsWithData = useCallback(
    (geometry: Geometry): string[] => {
      const intersectingIds: string[] = [];

      renderedMarkerData.forEach((value) => {
        if (value.type === 'geojson' && value.geometry) {
          const intersection = booleanIntersects(value.geometry, geometry);
          if (intersection) {
            intersectingIds.push(value.id);
          }
        } else if ((value as KineticMarkerData).coordinates) {
          const intersection = booleanIntersects(
            {
              type: 'Point',
              coordinates: (value as KineticMarkerData).coordinates as [number, number],
            },
            geometry
          );
          if (intersection) {
            intersectingIds.push(value.id);
          }
        }
      });
      return intersectingIds;
    },
    [renderedMarkerData]
  );

  const dataContext = useMemo(
    () => {
      return {
        data: renderedMarkerData,
        mapMarkers,
        intersectsWithData,
        lastUpdate,
      };
    },
    // only update the value when we've decided there is an update
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastUpdate]
  );

  const functionsContext = useMemo(() => {
    return {
      addLayer,
      updateData,
      addToCache,
      removeLayer,
    };
  }, [addLayer, updateData, addToCache, removeLayer]);

  return { dataContext, functionsContext };
};
